/**
 * Shared inline-edit Tasks Table component.
 *
 * Extracted from `routes/tasks.table.tsx` in PR #41 so both the Tasks
 * Table tab and the new Tasks Today landing render rows through the
 * same widget. The Today view passes its filtered Frogs and Due-today
 * subsets without the status or type columns; the Table tab keeps the
 * full column set.
 *
 * The component owns every cell editor (title input, type / priority /
 * status pill selects, due-date popover, delete) plus the per-column
 * sort toggle. Each cell dispatches a single-field PATCH on commit so a
 * row change touches one wire request rather than the whole row.
 *
 * Behaviour summary:
 *   - leading OPEN button opens the slide-over for the audit log
 *   - title cell is a controlled `<input>` that commits on blur or Enter
 *   - type + priority cells are coloured pill `<select>`s sharing the
 *     palette in `pills.tsx`
 *   - due cell is the Radix popover-based `<DueDateCell />`
 *   - status cell respects the `STATUS_TRANSITIONS` table so the user
 *     cannot pick a forward move the backend would reject
 *   - trailing X button soft-deletes via `useDeleteTask`
 *   - the `showStatusColumn` / `showTypeColumn` props let the caller
 *     drop columns the context makes redundant (the Today sections
 *     already imply the filter, so the status column is hidden there)
 *
 * UAT-47 dropped the leading completion-checkbox column that the
 * Round-5 rewrite (UAT-44) had introduced. Completion now happens only
 * on the Board view's card affordance — the table no longer carries any
 * checkbox cell. The strikethrough behaviour on closed rows stays
 * because it still helps the read on the Table tab's completed
 * sections.
 *
 * UAT-52 (Round 8 PR #52) reworks the closed-row visual: the
 * `line-through` class stays on the title cell so the priority and due
 * pills keep their crisp text, but the whole `<tr>` now picks up the
 * `opacity-65` dim. The earlier shape limited both classes to the title
 * cell, which read as "almost not struck through" at a glance because
 * the rest of the row stayed at full intensity. Dimming the row as a
 * single block carries the "this is closed" signal at distance while
 * the title strikethrough keeps the per-row treatment for readers who
 * scan column-by-column.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useDeleteTask, useUpdateTask } from '@/lib/api/tasks';
import { useSlideOver } from '@/lib/slide-over';
import { STATUS_TRANSITIONS } from '@/lib/task-status-transitions';
import { cn } from '@/lib/utils';
import { DueDateCell } from '@/components/tasks/DueDateCell';
import { PRIORITY_COLOR, TYPE_COLOR } from '@/components/tasks/pills';
import {
  TASK_PRIORITY,
  TASK_STATUS,
  TASK_TYPE,
  type Task,
  type TaskPriority,
  type TaskStatus,
  type TaskType,
} from '@workspace/shared';

/** Column identifiers the header can sort by. */
type SortKey = 'title' | 'type' | 'status' | 'dueDate' | 'priority' | 'updatedAt';

/**
 * Shared Tailwind class for every inline cell editor (input / select).
 * Keeps the row legible at rest (transparent background, body grey
 * text) and lifts on focus with the orange focus ring so the active
 * cell is unambiguous when Peter tabs through the row.
 */
const CELL_INPUT_CLASS =
  'h-7 w-full bg-transparent px-mx-2 text-mx-body-sm text-mx-grey-300 focus:outline-none focus:bg-mx-grey-900 focus:ring-1 focus:ring-mx-orange/40';

/** Priority ordering used when the sort is on the priority column.
 * String sort would put CRITICAL after MEDIUM alphabetically; we need
 * the explicit severity order instead. */
const PRIORITY_ORDER: Record<TaskPriority, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

/** Pill-style select geometry shared by the Type and Priority cells.
 * The select itself carries the pill background colour (mirroring the
 * `<TypePill />` / `<PriorityPill />` chip) so the cell reads as a
 * coloured chip while staying directly editable. We strip the native
 * select appearance and pad to match the pill chrome (12px horizontal,
 * 3px vertical, body-sm semibold uppercase). */
const PILL_SELECT_BASE =
  'mono-label inline-flex w-full appearance-none items-center border px-mx-3 py-[3px] text-mx-body-sm font-semibold uppercase tracking-wider text-mx-white focus:outline-none focus:ring-1 focus:ring-mx-orange/60';

/**
 * Props for the shared TaskTable widget.
 *
 * `rows` is the already-filtered task array the caller wants to render
 * (the component sorts the supplied rows but does not refetch). The
 * `showStatusColumn` / `showTypeColumn` flags let context-specific
 * surfaces drop columns that the filter already implies — the Today
 * view hides the status column inside its Frogs / Due-today sections
 * because every row in those tables shares the same scheduling context.
 */
export interface TaskTableProps {
  /** Pre-filtered task list to render. The table sorts but never
   * refetches; callers own the data source. */
  rows: Task[];
  /** Hide the Status column when the caller's context already implies
   * it (e.g. the Today view's two scheduled sections). Default: shown. */
  showStatusColumn?: boolean;
  /** Hide the Type column. Default: shown. */
  showTypeColumn?: boolean;
  /** Copy shown in the table body when `rows` is empty. Defaults to
   * `No open tasks.` so the legacy Table view keeps its phrasing. */
  emptyCopy?: string;
}

/**
 * Inline-edit Tasks Table.
 *
 * Renders the supplied rows in a sortable table where every cell is a
 * controlled inline editor. The leading affordance opens the slide-over
 * for read-only detail; every other interaction commits a single-field
 * PATCH against the backend.
 */
export function TaskTable({
  rows,
  showStatusColumn = true,
  showTypeColumn = true,
  emptyCopy = 'No open tasks.',
}: TaskTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('dueDate');
  const [asc, setAsc] = useState(true);

  // Sort a copy of `rows` so the caller's source array stays untouched.
  // Re-sorts whenever the rows reference, sort key, or direction change.
  const sorted = useMemo(() => {
    const next = [...rows].sort((a, b) => {
      const cmp = compareTasks(a, b, sortKey);
      return asc ? cmp : -cmp;
    });
    return next;
  }, [rows, sortKey, asc]);

  /** Click handler for header sort buttons. Same key flips direction;
   * a new key resets to ascending. */
  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setAsc(!asc);
    } else {
      setSortKey(key);
      setAsc(true);
    }
  }

  // Column count tracks the visible cells so the empty-state row can
  // span the right number of columns. Always counts: open + title +
  // priority + due + updated + delete (6); plus type / status when on.
  // The Round-5 leading completion-checkbox column was dropped in
  // UAT-47 — completion now lives only on the Board view's card
  // affordance, so the table no longer carries the checkbox cell.
  const colSpan = 6 + (showTypeColumn ? 1 : 0) + (showStatusColumn ? 1 : 0);

  return (
    <table data-testid="tasks-table" className="w-full border border-mx-grey-800 text-mx-body-sm">
      <thead data-testid="tasks-table-head" className="bg-mx-card text-left text-mx-grey-mid">
        <tr>
          {/* Leading affordance column. Stays narrow; the icon button
              opens the slide-over for the audit log and any other
              read-only detail the table cannot surface. The Round-5
              completion checkbox column that previously sat to the left
              of this cell was removed in UAT-47 — completion now lives
              only on the Board view's card affordance. */}
          <th
            aria-label="Open detail"
            className="w-mx-7 border-b border-mx-grey-800 px-mx-2 py-mx-2"
          />
          <Th label="Title" sortKey="title" current={sortKey} asc={asc} onClick={toggleSort} />
          {showTypeColumn && (
            <Th label="Type" sortKey="type" current={sortKey} asc={asc} onClick={toggleSort} />
          )}
          <Th
            label="Priority"
            sortKey="priority"
            current={sortKey}
            asc={asc}
            onClick={toggleSort}
          />
          <Th label="Due" sortKey="dueDate" current={sortKey} asc={asc} onClick={toggleSort} />
          {showStatusColumn && (
            <Th label="Status" sortKey="status" current={sortKey} asc={asc} onClick={toggleSort} />
          )}
          <Th
            label="Updated"
            sortKey="updatedAt"
            current={sortKey}
            asc={asc}
            onClick={toggleSort}
          />
          {/* Trailing delete-button column. Stays narrow; the per-row
              cell renders an XMarkIcon button that soft-deletes the
              task via the archivedAt endpoint. PR #38. */}
          <th
            aria-label="Delete task"
            className="w-mx-7 border-b border-mx-grey-800 px-mx-2 py-mx-2"
          />
        </tr>
      </thead>
      <tbody>
        {sorted.map((task) => (
          <Row
            key={task.id}
            task={task}
            showStatusColumn={showStatusColumn}
            showTypeColumn={showTypeColumn}
          />
        ))}
        {sorted.length === 0 && (
          <tr>
            <td
              colSpan={colSpan}
              className="border-b border-mx-grey-800 px-mx-3 py-mx-4 text-center text-mx-body-sm text-mx-grey-mid"
            >
              {emptyCopy}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/**
 * Comparator for the active sort column. Returns a stable numeric
 * difference (negative, zero, positive) so the `asc` flag flips the
 * sign on the caller side.
 */
function compareTasks(a: Task, b: Task, key: SortKey): number {
  if (key === 'priority') {
    return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
  }
  if (key === 'dueDate') {
    // Null due dates sort to the bottom so the user sees what is
    // actually scheduled first.
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.localeCompare(b.dueDate);
  }
  if (key === 'updatedAt') {
    return a.updatedAt.localeCompare(b.updatedAt);
  }
  const av = String(a[key] ?? '');
  const bv = String(b[key] ?? '');
  return av.localeCompare(bv);
}

/** Sortable column header. */
function Th({
  label,
  sortKey,
  current,
  asc,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  asc: boolean;
  onClick: (k: SortKey) => void;
}) {
  const active = current === sortKey;
  return (
    <th className="border-b border-mx-grey-800 px-mx-3 py-mx-2">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className="mono-label text-mx-grey-mid hover:text-mx-grey-300"
      >
        {label}
        {active && <span className="ml-mx-1 text-mx-orange">{asc ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

/**
 * One row in the inline-edit table.
 *
 * The row owns nothing beyond layout; each cell renders its own
 * controlled editor and dispatches its own single-field PATCH. The
 * leading "OPEN" button is the only way to reach the slide-over from
 * the table because clicking a cell now means "I want to edit this
 * field" rather than "I want to see the detail panel".
 */
function Row({
  task,
  showStatusColumn,
  showTypeColumn,
}: {
  task: Task;
  showStatusColumn: boolean;
  showTypeColumn: boolean;
}) {
  const openSlideOver = useSlideOver((s) => s.open);
  const done = task.completedAt !== null;
  return (
    <tr
      data-testid="tasks-table-row"
      data-completed={done ? 'true' : 'false'}
      className={cn(
        'border-b border-mx-grey-800 hover:bg-mx-grey-900',
        // UAT-52 / UAT-53: dim the whole row on completed tasks so the
        // "closed" signal reads at distance. The strikethrough lives on
        // the title input itself (see TitleCell) rather than the parent
        // <td> because `<input>` elements do not inherit
        // text-decoration from their parent — putting `line-through`
        // on the cell visibly struck through whitespace but left the
        // editable input untouched.
        done && 'opacity-65',
      )}
    >
      <td className="px-mx-2 py-mx-2 align-middle">
        <button
          type="button"
          aria-label={`Open task ${task.title}`}
          onClick={() => openSlideOver({ kind: 'task', taskId: task.id })}
          className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
        </button>
      </td>
      <td className="px-mx-1 py-mx-1 align-middle" data-testid="title-cell">
        <TitleCell task={task} done={done} />
      </td>
      {showTypeColumn && (
        <td className="mono-label px-mx-1 py-mx-1 align-middle">
          <TypeCell task={task} />
        </td>
      )}
      <td className="mono-label px-mx-1 py-mx-1 align-middle">
        <PriorityCell task={task} />
      </td>
      <td className="mono-label tnum px-mx-1 py-mx-1 align-middle">
        <DueDateCell task={task} />
      </td>
      {showStatusColumn && (
        <td className="mono-label px-mx-1 py-mx-1 align-middle">
          <StatusCell task={task} />
        </td>
      )}
      <td className="mono-label tnum px-mx-3 py-mx-2 align-middle text-mx-grey-mid">
        {task.updatedAt.slice(0, 10)}
      </td>
      <td className="px-mx-2 py-mx-2 align-middle">
        <DeleteCell task={task} />
      </td>
    </tr>
  );
}

/**
 * Inline-editable title cell.
 *
 * Mirrors the slide-over title input: a local `draft` string holds the
 * in-flight value so the cursor does not jump while the user types;
 * blur (or Enter) commits the trimmed value if it changed. Enter also
 * moves focus to the next row's title input so Peter can fly down the
 * column without reaching for the mouse.
 *
 * UAT-53: `done` controls the strikethrough class on the input itself.
 * `<input>` elements do not inherit `text-decoration` from their
 * parent, so putting `line-through` on the surrounding `<td>` looked
 * struck through against whitespace but left the editable text alone.
 * The class lives on the input so the strikethrough actually renders.
 */
function TitleCell({ task, done }: { task: Task; done: boolean }) {
  const updateTask = useUpdateTask();
  const [draft, setDraft] = useState(task.title);

  // Reseed the draft when the row identity changes (re-sort can swap
  // which task this cell renders) or when the server returns a fresh
  // `updatedAt` (another tab edited the row). Depending on `task.title`
  // here would clobber an in-progress edit between keystrokes, mirroring
  // the slide-over's controlled-input sync pattern from PR #25.
  useEffect(() => {
    setDraft(task.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, task.updatedAt]);

  return (
    <input
      type="text"
      aria-label={`Task title for ${task.title}`}
      data-task-id={task.id}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim();
        if (next && next !== task.title) {
          updateTask.mutate({ id: task.id, patch: { title: next } });
        } else if (!next) {
          // Empty title is invalid; revert the draft so the cell does
          // not stay visually blank while the server state stays sane.
          setDraft(task.title);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          // Blur first so the onBlur commit fires; then advance focus
          // to the next row's title input. The selector matches every
          // title cell across the table and we pick the one after the
          // current row.
          const inputs = Array.from(
            document.querySelectorAll<HTMLInputElement>('input[data-task-id]'),
          );
          const idx = inputs.indexOf(e.currentTarget);
          e.currentTarget.blur();
          const next = inputs[idx + 1];
          if (next) next.focus();
        }
      }}
      className={cn(CELL_INPUT_CLASS, done && 'line-through')}
    />
  );
}

/**
 * Inline-editable type cell.
 *
 * Renders as a coloured pill-shaped `<select>` that mirrors the
 * `<TypePill />` component on the kanban board, sharing the same
 * `TYPE_COLOR` map. PR #38 swapped the previous transparent select for
 * this pill style so the table reads at a glance — the type colour
 * carries the semantic without forcing Peter to scan a mono-label
 * column. Commits on change without a draft buffer because the
 * select cannot mid-edit.
 */
function TypeCell({ task }: { task: Task }) {
  const updateTask = useUpdateTask();
  return (
    <select
      aria-label={`Task type for ${task.title}`}
      value={task.type}
      onChange={(e) =>
        updateTask.mutate({ id: task.id, patch: { type: e.target.value as TaskType } })
      }
      data-testid="type-cell-select"
      data-type={task.type}
      className={cn(PILL_SELECT_BASE, TYPE_COLOR[task.type])}
    >
      {TASK_TYPE.map((t) => (
        <option key={t} value={t} className="bg-mx-card text-mx-grey-300">
          {t}
        </option>
      ))}
    </select>
  );
}

/**
 * Inline-editable priority cell.
 *
 * Same pill-shaped select pattern as `TypeCell`, tinted via the
 * `PRIORITY_COLOR` map from the pills module so the table cell and the
 * board pill share the exact palette. The fly-out options drop back to
 * the dark card surface so the dropdown menu is legible without
 * forcing the user to read white-on-bright-red text.
 */
function PriorityCell({ task }: { task: Task }) {
  const updateTask = useUpdateTask();
  return (
    <select
      aria-label={`Task priority for ${task.title}`}
      value={task.priority}
      onChange={(e) =>
        updateTask.mutate({ id: task.id, patch: { priority: e.target.value as TaskPriority } })
      }
      data-testid="priority-cell-select"
      data-priority={task.priority}
      className={cn(PILL_SELECT_BASE, PRIORITY_COLOR[task.priority])}
    >
      {TASK_PRIORITY.map((p) => (
        <option key={p} value={p} className="bg-mx-card text-mx-grey-300">
          {p}
        </option>
      ))}
    </select>
  );
}

/**
 * Trailing per-row delete cell.
 *
 * Renders the small XMarkIcon button that soft-deletes the task via
 * the `useDeleteTask` mutation (backend writes `archivedAt` so nothing
 * is lost permanently). No confirm modal — the table is dense and the
 * undo path is the audit log + a future restore flow. PR #38.
 */
function DeleteCell({ task }: { task: Task }) {
  const deleteTask = useDeleteTask();
  return (
    <button
      type="button"
      aria-label={`Delete task ${task.title}`}
      data-testid="delete-task"
      onClick={() => deleteTask.mutate(task.id)}
      className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-neg"
    >
      <XMarkIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
    </button>
  );
}

/**
 * Inline-editable status cell. Mirrors the slide-over: options that
 * are not a valid onward move from the current status are rendered
 * `disabled` so Peter cannot trigger a backend 400. A defensive guard
 * inside the change handler also bails out if a disabled option fires
 * for any reason (some browsers still raise `change` on disabled
 * options in edge cases).
 */
function StatusCell({ task }: { task: Task }) {
  const updateTask = useUpdateTask();
  const allowed = STATUS_TRANSITIONS[task.status];
  return (
    <select
      aria-label={`Task status for ${task.title}`}
      value={task.status}
      onChange={(e) => {
        const next = e.target.value as TaskStatus;
        if (!allowed.includes(next)) return;
        if (next === task.status) return;
        updateTask.mutate({ id: task.id, patch: { status: next } });
      }}
      className={cn(CELL_INPUT_CLASS, 'appearance-none')}
    >
      {TASK_STATUS.map((s) => {
        const ok = allowed.includes(s);
        return (
          <option key={s} value={s} disabled={!ok}>
            {s}
            {!ok ? ' (blocked)' : ''}
          </option>
        );
      })}
    </select>
  );
}
